import {AxiosError} from "axios";
import {ApiConfig} from "components/src/api/admin/ApiConfig";
import {MTAApi, PostfixMap} from "components/src/api/admin/generated";
import {ActionsDropdown} from "components/src/common/ActionsDropdown";
import {ClearSelection} from "components/src/common/ClearSelection";
import {ConfirmDialog} from "components/src/common/ConfirmDialog";
import {DialogCloseCause} from "components/src/common/Enums";
import {InvertSelection} from "components/src/common/InvertSelection";
import {Truncate} from "components/src/common/Truncate";
import {useSpinnerStatusContext} from "components/src/hook/UseSpinnerStatusContext";
import {useTranslationWithOverride} from "components/src/hook/UseTranslationWithOverride";
import {PageAlert} from "components/src/layout/PageAlert.tsx";
import {PageContent} from "components/src/layout/PageContent.tsx";
import {PageHeader} from "components/src/layout/PageHeader.tsx";
import {logger} from "components/src/utils/Logger";
import {Utils} from "components/src/utils/Utils";
import {AtSign as MTAIcon, Bin as DeleteIcon, Plus as AddMapIcon} from "iconoir-react";
import {useEffect, useState} from 'react'
import {Dropdown} from "react-bootstrap";
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Nav from "react-bootstrap/Nav";
import Navbar from 'react-bootstrap/Navbar';
import Row from 'react-bootstrap/Row';
import Table from 'react-bootstrap/Table';
import {useNavigate} from "react-router-dom";
import {MTAMapPopup} from "./MTAMapPopup";

/*
 * REST api for accessing the back-end
 */
const mtaApi = new MTAApi(ApiConfig.config());

type OnClick = () => void;

interface ColumnProps {
    value?: string
    children?: React.ReactNode
    className? : string
    maxLength? : number
    onClick? : OnClick
}

const Column = ({value, children, className, maxLength = 60, onClick}: ColumnProps) => {
    return (
        <td onClick={onClick} className={"cursor-pointer " + className}>{children}<Truncate value={value} maxLength={maxLength}/></td>
    )
}

export const MTAMaps = () => {
    /**
     * Navigate is used to navigate to the login page if the user is no longer authenticated or to some other page
     * if the dialog is closed
     */
    const navigate = useNavigate();
    /**
     * The maps
     */
    const [maps, setMaps] = useState<PostfixMap[]>([]);
    /**
     * The selected maps
     */
    const [selectedMaps, setSelectedMaps] = useState<Array<PostfixMap>>([]);
    /**
     * The error message if some general error occurred
     */
    const [errorMessage, setErrorMessage] = useState<string>();
    /**
     * The clicked map item
     */
    const [clickedMap, setClickedMap] = useState<PostfixMap>();
    /**
     * If true, the items will be reloaded from the back-end
     */
    const [forceReload, setForceReload] = useState<boolean>(false);
    /**
     * If true, the "confirm deletion" modal dialog will be shown
     */
    const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);
    /**
     * If true, the "edit map dialog" modal dialog will be shown
     */
    const [showEditMapDialog, setShowEditMapDialog] = useState<boolean>(false);
    /**
     * If true, the "add map dialog" modal dialog will be shown
     */
    const [showAddMapDialog, setShowAddMapDialog] = useState<boolean>(false);
    /**
     * Keeps track of activities for which the spinner should be shown. If an activity is started, the counter should
     * be increased by one and after the activity
     */
    const {setSpinnerStateCounter} = useSpinnerStatusContext();

    /**
     * i18next translation hook
     */
    const {t} = useTranslationWithOverride();

    useEffect(() =>
    {
        logger.debug("Load maps")

        // reset state when new data is loaded
        setErrorMessage(undefined);
        setSelectedMaps([]);
        setForceReload(false);

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        mtaApi.mTAControllerListMaps()
            .then(response => {
                setMaps(response.data);
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error getting maps", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            })
    }, [navigate,
        forceReload,
        setSpinnerStateCounter,
        t
    ]);

    const editMap = (map : PostfixMap) =>
    {
        setClickedMap(map);
        setShowEditMapDialog(true);
    }

    /**
     * Is called when the EditMapDialog is closed
     */
    const onEditMapDialogClose = () => {
        setShowEditMapDialog(false);
    }

    /**
     * Is called when the EditMapDialog is closed
     */
    const onAddMapDialogClose = (closeCause : DialogCloseCause) =>
    {
        setShowAddMapDialog(false);

        if (closeCause === DialogCloseCause.Succes) {
            setForceReload(true);
        }
    }

    const deleteSelectedMaps = () =>
    {
        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        for (const map of selectedMaps)
        {
            mtaApi.mTAControllerDeleteMap(map.type, map.name)
                .then(() => {
                    setForceReload(true);
                })
                .catch((err: Error | AxiosError) => {
                    logger.error("Error deleting map", err);
                    Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                    setErrorMessage(Utils.getErrorMessage(err, t));
                })
                .finally(() => {
                    if (setSpinnerStateCounter !== undefined) {
                        setSpinnerStateCounter(count => count - 1);
                    }
                    setShowDeleteConfirm(false);
                    setClickedMap(undefined);
                })
        }
    }

    const invertSelection = () => {
        setSelectedMaps([...maps].filter(m => !selectedMaps.includes(m)));
    }

    /**
     * Called when a map is selected (i.e., when the checkbox is checked)
     */
    const onSelectMap = (checked : boolean, map : PostfixMap) =>
    {
        setSelectedMaps(checked ?
            [...selectedMaps, map] :
            selectedMaps.filter(m => m.type !== map.type || m.name !== map.name)
        );
    }

    return (
        <>
            <ConfirmDialog title={t("mta-maps.confirm")}
                           bodyText={t("mta-maps.body-text")}
                           confirmButtonTitle={t("mta-maps.delete")}
                           onClickConfirmEvent={deleteSelectedMaps}
                           showConfirm={showDeleteConfirm}
                           setShowConfirm={setShowDeleteConfirm}
            />

            {showAddMapDialog && <MTAMapPopup onClose={onAddMapDialogClose} />}
            {showEditMapDialog && clickedMap !== undefined && <MTAMapPopup editType={clickedMap.type} editName={clickedMap.name} onClose={onEditMapDialogClose} />}

            <div className="d-flex flex-column h-100">
                <PageHeader icon={<MTAIcon />} title={t("mta-maps.title")} onClickReload={() => setForceReload(true)}/>

                <PageAlert message={errorMessage} setMessage={setErrorMessage}/>

                <div className="px-4">
                    <Row>
                        <div className="d-flex flex-row align-items-center mb-4">
                            <div className="d-flex flex-fill justify-content-end align-items-center">
                                <ActionsDropdown>
                                    <Dropdown.Item onClick={() => setShowAddMapDialog(true)}>
                                        <AddMapIcon className="text-success"/> {t("mta-maps.add-map")}
                                    </Dropdown.Item>
                                </ActionsDropdown>
                            </div>
                        </div>
                    </Row>
                </div>

                <PageContent className="flex-grow-1 min-h-0">
                    <Row className="h-100 w-100">
                        <Col className="h-100 overflow-auto">
                            <Table className="sticky sticky-x" bordered hover>
                                <thead className="sticky-top">
                                <tr className="text-nowrap">
                                    {selectedMaps.length === 0 ?
                                        <>
                                            <th className="th-w-min"><InvertSelection invertSelectionEvent={invertSelection}/></th>
                                            <th>{t("mta-maps.name")}</th>
                                            <th>{t("mta-maps.type")}</th>
                                            <th>{t("mta-maps.filename")}</th>
                                        </>
                                        :
                                        <th colSpan={4}>
                                            <Navbar className="bg-body-tertiary ps-2">
                                                {/* Add icon to first column which will clear the selection when clicked */}
                                                <ClearSelection clearSelectionEvent={() => setSelectedMaps([])} checked={true}/>

                                                <Navbar.Text className="ms-2">{selectedMaps.length} selected</Navbar.Text>

                                                <div className="vr ms-4 me-2"></div>

                                                <Nav>
                                                    <Nav.Link onClick={() => setShowDeleteConfirm(true)}>
                                                        <DeleteIcon className="text-danger"/> {t("mta-maps.delete-map")}
                                                    </Nav.Link>
                                                </Nav>
                                            </Navbar>
                                        </th>
                                    }
                                </tr>
                                </thead>
                                <tbody>
                                {maps.map((m: PostfixMap) => (
                                    <tr key={m.filename} className="text-nowrap">
                                        <Column className="td-w-min align-middle">
                                            <Form.Check
                                                type="checkbox"
                                                className="me-0"
                                                checked={selectedMaps.find((smap) =>  smap.type === m.type && smap.name == m.name) !== undefined}
                                                onChange={(e) => onSelectMap(e.target.checked, m)}
                                                inline
                                            />
                                        </Column>
                                        <Column value={m.name} onClick={() => editMap(m)}/>
                                        <Column value={m.type} onClick={() => editMap(m)}/>
                                        <Column value={m.filename + ".map"} onClick={() => editMap(m)}/>
                                    </tr>
                                ))}
                                </tbody>
                            </Table>
                        </Col>
                    </Row>
                </PageContent>
            </div>
        </>
    );
}
